import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormInput,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { ISettingsGeneral } from "../../../../../models/settings";
import SettingsService from "../../../../../services/settings.service";
import SettingsPageContainer from "../../../../../components/Settings/SettingsPageContainer";
import {
  TSettingsCentralBankForm,
  settingsCentralBankSchema,
} from "./SettingsCentralBankSchema";
import { useAppSelector } from "../../../../../store/store";

const translationPath = "components.settingsGeneral.";

const service = new SettingsService();

const SettingsCentralBank = () => {
  const { t } = useTranslation();
  const [userSettings, setUserSettings] = useState<ISettingsGeneral>();
  const methods = useForm<TSettingsCentralBankForm>({
    resolver: zodResolver(settingsCentralBankSchema),
  });
  const { setMessage } = useNotificationContext();
  const { sendRequest } = useFetch(service.getSettingsGeneral);
  const { sendRequest: update, loading } = useFetch(
    service.updateSettingsGeneral
  );
  const generalSettings = useAppSelector(
    (state) => state.settingsGeneral.settings
  );

  const onLoadSettingsGeneral = useCallback(async () => {
    const { data } = await sendRequest(undefined);
    if (data) {
      setUserSettings(data);
    }
  }, [sendRequest]);

  useEffect(() => {
    onLoadSettingsGeneral();
  }, [onLoadSettingsGeneral]);

  useEffect(() => {
    if (userSettings) {
      methods.reset({
        ...userSettings,
      });
    }
  }, [userSettings, methods]);

  const onSubmitHandler = useCallback(
    async (values: TSettingsCentralBankForm) => {
      const { success } = await update({
        item: {
          ...values,
          cepCacheTime: Number(generalSettings.cepCacheTime),
          correiosPageSize: Number(generalSettings.correiosPageSize),
          apiGeocodingKey: generalSettings.apiGeocodingKey,
          correiosAddressesUrl: generalSettings.correiosAddressesUrl,
          correiosAuthenticationUrl: generalSettings.correiosAuthenticationUrl,
          correiosLocationsUrl: generalSettings.correiosLocationsUrl,
          correiosTokenAccessCode: generalSettings.correiosTokenAccessCode,
          correiosTokenPostCard: generalSettings.correiosTokenPostCard,
          correiosTokenUser: generalSettings.correiosTokenUser,
          correiosUrl: generalSettings.correiosUrl,
        },
      });
      if (success) {
        setMessage({
          message: `${t(translationPath + "success")}`,
          type: "success",
          notificationKey: new Date().getMilliseconds(),
        });
      }
    },
    [update, setMessage, t, generalSettings]
  );

  const { handleSubmit } = methods;

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <SettingsPageContainer title="Configurações Banco Central">
      <Form {...methods}>
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitHandler)}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: matches ? "100%" : "70%",
            mt: 2,
          }}
        >
          <Grid container columnSpacing={2} mt={5}>
            <Grid item xs={12} sm={12} mb={2}>
              <FormInput
                size="small"
                name="bankBaseUrl"
                fullWidth
                label={t(translationPath + "bank_base")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="bankBancosUrl"
                fullWidth
                label={t(translationPath + "bank_bancos")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="bankCooperativasUrl"
                fullWidth
                label={t(translationPath + "bank_cooperativas")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <Button
                variant="contained"
                disabled={loading ? true : false}
                type="submit"
                size="small"
              >
                {loading && (
                  <Stack
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <CircularProgress size={18} />
                    {t(translationPath + "saving")}
                  </Stack>
                )}
                {!loading && t(translationPath + "save")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Form>
    </SettingsPageContainer>
  );
};

export default SettingsCentralBank;
